// src/api/watch.js
//
// Thin client for the proxy's watch-mode REST endpoints
// (proxy-server/apiServer.js — docs/signals-recommendations-watch-spec.md §6,
// proxy-server/watch/{alertStore,watchConfig,sinks}.js). Same fetch idiom as
// api/checks.js: PROXY_API_BASE resolves the proxy REST host (prod:
// REACT_APP_PROXY_API_URL; local dev: relative `/proxy`), routes are always
// prefixed with `/proxy` on top of that base.
//
//   GET  /proxy/watch/alerts?limit=   -> { alerts: [...] }  (ring, newest first)
//   GET  /proxy/watch-config          -> { config, defaults, detectorIds }
//   POST /proxy/watch-config          { ...partial } -> { ok, config }
//                                        400 { error: 'unknown_detector_id', ids }
//   POST /proxy/watch/test-webhook    -> { ok, sent, webhookUrl, alert }
//                                        400 { error: 'no_webhook_configured' }

const PROXY_API_BASE = (process.env.REACT_APP_PROXY_API_URL || '/proxy').replace(/\/$/, '');

/**
 * Internal: POST/GET JSON against proxy REST API. Always resolves parsed
 * JSON body on 2xx. On non-2xx response, rejects with an Error whose
 * `.status` and `.body` carry the server's status code + parsed error body
 * (mirrors api/checks.js's request() helper exactly).
 */
async function request(path, init = {}) {
  let res;
  try {
    res = await fetch(`${PROXY_API_BASE}/proxy${path}`, {
      ...init,
      headers: {
        Accept: 'application/json',
        ...(init.body ? { 'Content-Type': 'application/json' } : {}),
        ...(init.headers || {}),
      },
    });
  } catch (e) {
    const err = new Error(`Could not reach the proxy server at ${PROXY_API_BASE}`);
    err.status = 0;
    err.body = null;
    err.cause = e;
    throw err;
  }

  let json = null;
  try { json = await res.json(); } catch { /* empty/non-JSON body */ }

  if (!res.ok) {
    const err = new Error((json && (json.detail || json.error)) || `${init.method || 'GET'} ${path} failed (${res.status})`);
    err.status = res.status;
    err.body = json;
    throw err;
  }
  return json;
}

/**
 * GET /proxy/watch/alerts?limit= -> alert[] (newest first). Returns [] on a
 * malformed/empty response rather than throwing, since this is polled/refreshed
 * from a lightweight UI panel.
 */
export async function getWatchAlerts(limit = 50) {
  const data = await request(`/watch/alerts?limit=${encodeURIComponent(limit)}`, { method: 'GET' });
  return Array.isArray(data?.alerts) ? data.alerts : [];
}

/**
 * GET /proxy/watch-config -> { config, defaults, detectorIds }. Returns the
 * server shape as-is so callers can badge/gate per-detector controls.
 */
export async function getWatchConfig() {
  return request('/watch-config', { method: 'GET' });
}

/**
 * POST /proxy/watch-config { ...partial } -> { ok, config }. Partial update —
 * only the fields present in `patch` are changed server-side. On a 400 the
 * promise rejects with err.body = { error: 'unknown_detector_id', ids: [...] }.
 */
export async function setWatchConfig(patch = {}) {
  return request('/watch-config', {
    method: 'POST',
    body: JSON.stringify(patch),
  });
}

/**
 * Convenience wrapper: mute/unmute a single detector id via a
 * { detectors: { [id]: { enabled } } } partial patch.
 */
export async function setDetectorEnabled(detectorId, enabled) {
  return setWatchConfig({ detectors: { [detectorId]: { enabled: !!enabled } } });
}

/**
 * POST /proxy/watch/test-webhook -> { ok, sent, webhookUrl, alert }. Fires a
 * sample alert through the webhook path regardless of shadow mode. On a 400
 * the promise rejects with err.body = { error: 'no_webhook_configured' }.
 */
export async function testWatchWebhook() {
  return request('/watch/test-webhook', { method: 'POST' });
}
